import { Controller } from "@hotwired/stimulus"
import 'ol/ol.css';
import { Map, View } from 'ol';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import {OSM} from 'ol/source';
import { fromLonLat } from 'ol/proj';
import { Point } from 'ol/geom';
import { Feature } from 'ol';
import { Vector as VectorSource } from 'ol/source';
import { Icon, Style } from 'ol/style';
import { defaults as defaultInteractions } from 'ol/interaction';
import {DEFAULT_COORDINATES} from "../js/constants";
import markerUrl from '../assets/icons/map-marker.svg';

export default class extends Controller {
    static targets = ["map"];
    static values = { coordinates: String, color: String };

    declare readonly mapTarget: HTMLElement;
    declare readonly coordinatesValue: string;
    declare readonly colorValue: string;

    private map: Map | null = null;
    private marker: Feature | null = null;
    private vectorSource: VectorSource | null = null;

    /**
     * Attempts to read and parse the WKT coordinate string from the hidden field.
     * Expected format: "POINT(lng lat)"
     * Returns an object with { lng, lat } if parsing succeeds, or null otherwise.
     */
    parseWKTCoordinate(): { lng: number, lat: number } {
        if (!this.coordinatesValue) {
            return DEFAULT_COORDINATES;
        }

        const regex = /POINT ?\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/;
        const match = this.coordinatesValue.match(regex);

        if (match && match.length === 3) {
            const lng = parseFloat(match[1]);
            const lat = parseFloat(match[2]);
            if (!isNaN(lng) && !isNaN(lat)) {
                return { lng, lat };
            }
        }
        return DEFAULT_COORDINATES;
    }

    connect() {
        let coords = this.parseWKTCoordinate();
        const { lng, lat } = coords;

        this.marker = new Feature({
            geometry: new Point(fromLonLat([lng, lat]))
        });

        const style = new Style({
            image: new Icon({
                src: markerUrl,
                scale: 0.3, // Adjust the scale of the icon
                anchor: [0.5, 0.5],
            })
        });

        // Style the marker with an icon
        this.marker.setStyle(style);

        // Create a vector source and layer for the marker
        this.vectorSource = new VectorSource({
            features: [this.marker]
        });

        const vectorLayer = new VectorLayer({
            source: this.vectorSource
        });

        // Initialize the map
        this.map = new Map({
            target: this.mapTarget,
            layers: [
                new TileLayer({
                    source: new OSM(),
                    className: 'osm-tile-layer'
                }),
                vectorLayer
            ],
            view: new View({
                center: fromLonLat([lng, lat]),
                zoom: 14
            }),
            interactions: defaultInteractions()
        });
    }

    disconnect() {
        this.map?.dispose();
    }
}
